import { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  BomWithLines,
  ManufacturingProduct,
  ProductionOrderWithWorkOrders,
} from '../../types/manufacturing';

interface Props {
  // products kept in the prop shape so the page wiring doesn't change; the
  // current Sankey only needs boms + production orders. Underscore-prefix
  // tells eslint it's intentionally unused.
  products?: ManufacturingProduct[];
  boms: BomWithLines[];
  productionOrders: ProductionOrderWithWorkOrders[];
}

// Sankey color palette — deliberately a richer, dashboard-ish palette than
// the rest of the app. Manufacturing is a separate visual context.
const PALETTE = {
  raw: '#64748b',
  sub: '#f97316',
  bomSub: '#f97316',
  bomFinal: '#10b981',
  draft: '#cbd5e1',
  confirmed: '#fbbf24',
  progress: '#3b82f6',
  to_close: '#0ea5e9',
  done: '#22c55e',
  cancel: '#ef4444',
  other: '#a78bfa',
};

interface SNode {
  id: string;
  stage: number;
  label: string;
  color: string;
  value: number;
  // Layout (px)
  x: number;
  y: number;
  height: number;
}

interface SLink {
  source: string;
  target: string;
  value: number;
}

interface Stage {
  id: string;
  label: string;
}

const STAGES: Stage[] = [
  { id: 'inputs', label: 'Inputs' },
  { id: 'boms', label: 'Bills of materials' },
  { id: 'orders', label: 'Production orders' },
  { id: 'workorders', label: 'Work orders' },
];

const NODE_W = 30;
const STAGE_GAP = 240;
const PAD_TOP = 44;
const PAD_BOTTOM = 28;
const HEIGHT = 560;
const PAD_LEFT = 160;
const PAD_RIGHT = 180;
// Min vertical separation between two labels in the same stage. Drives
// the leader-line collision avoidance below.
const LABEL_LINE_H = 34;

function buildModel(
  boms: BomWithLines[],
  productionOrders: ProductionOrderWithWorkOrders[]
): { nodes: SNode[]; links: SLink[] } {
  // ----- classification -----
  const productIdsWithBom = new Set(boms.map((b) => b.product_id));
  const productIdsAsLines = new Set<string>();
  for (const b of boms) for (const l of b.bom_lines) productIdsAsLines.add(l.product_id);

  // Inputs split: raw (no BOM) vs sub-assemblies (have BOM AND used as lines)
  const isRaw = (pid: string) => !productIdsWithBom.has(pid);
  // BOM split: sub (its product is also a line elsewhere) vs final (top-level)
  const isSubBom = (b: BomWithLines) => productIdsAsLines.has(b.product_id);

  // ----- count flows -----
  // Inputs → BOMs: for each bom_line, classify source (raw / sub) and target (subBom / finalBom).
  let rawToSubBom = 0,
    rawToFinalBom = 0,
    subToSubBom = 0,
    subToFinalBom = 0;
  const bomById = new Map(boms.map((b) => [b.id, b]));
  for (const b of boms) {
    const targetIsSub = isSubBom(b);
    for (const l of b.bom_lines) {
      const fromRaw = isRaw(l.product_id);
      if (fromRaw && targetIsSub) rawToSubBom++;
      else if (fromRaw && !targetIsSub) rawToFinalBom++;
      else if (!fromRaw && targetIsSub) subToSubBom++;
      else subToFinalBom++;
    }
  }

  // BOMs → Production orders: bucket by source-BOM type and target state.
  // Mirrors PRODUCTION_STATE_COLUMNS so the Sankey doesn't bury 'to_close'
  // POs in 'Other' — that misrepresents shop floors with QC-gated workflow.
  const STATES_OF_INTEREST = ['draft', 'confirmed', 'progress', 'to_close', 'done', 'cancel'];
  const stateLabel: Record<string, string> = {
    draft: 'Draft',
    confirmed: 'Confirmed',
    progress: 'In progress',
    to_close: 'To close',
    done: 'Done',
    cancel: 'Cancelled',
    other: 'Other',
  };
  const subBomToState: Record<string, number> = {};
  const finalBomToState: Record<string, number> = {};
  for (const s of STATES_OF_INTEREST) {
    subBomToState[s] = 0;
    finalBomToState[s] = 0;
  }
  subBomToState.other = 0;
  finalBomToState.other = 0;
  for (const po of productionOrders) {
    const bom = po.bom_id ? bomById.get(po.bom_id) : undefined;
    const fromSub = bom ? isSubBom(bom) : false;
    const stateKey = STATES_OF_INTEREST.includes(po.state) ? po.state : 'other';
    if (fromSub) subBomToState[stateKey]++;
    else finalBomToState[stateKey]++;
  }

  // Production orders → Work orders: bucket by source PO state, summed work-order count.
  const stateToWoCount: Record<string, number> = {};
  for (const s of [...STATES_OF_INTEREST, 'other']) stateToWoCount[s] = 0;
  for (const po of productionOrders) {
    const stateKey = STATES_OF_INTEREST.includes(po.state) ? po.state : 'other';
    stateToWoCount[stateKey] += po.work_orders.length;
  }

  // ----- nodes -----
  // Stage values are determined by the SUM of incoming or outgoing flows
  // (whichever is larger — for the leftmost stage, outgoing).
  const rawValue = rawToSubBom + rawToFinalBom;
  const subValue = subToSubBom + subToFinalBom;
  const subBomValue = rawToSubBom + subToSubBom;
  const finalBomValue = rawToFinalBom + subToFinalBom;

  const nodes: SNode[] = [
    {
      id: 'in:raw',
      stage: 0,
      label: 'Raw materials',
      color: PALETTE.raw,
      value: rawValue,
      x: 0,
      y: 0,
      height: 0,
    },
    {
      id: 'in:sub',
      stage: 0,
      label: 'Sub-assemblies',
      color: PALETTE.sub,
      value: subValue,
      x: 0,
      y: 0,
      height: 0,
    },
    {
      id: 'bom:sub',
      stage: 1,
      label: 'Sub BOMs',
      color: PALETTE.bomSub,
      value: subBomValue,
      x: 0,
      y: 0,
      height: 0,
    },
    {
      id: 'bom:final',
      stage: 1,
      label: 'Final BOMs',
      color: PALETTE.bomFinal,
      value: finalBomValue,
      x: 0,
      y: 0,
      height: 0,
    },
  ];

  for (const s of [...STATES_OF_INTEREST, 'other'] as const) {
    const v = (subBomToState[s] || 0) + (finalBomToState[s] || 0);
    if (v === 0) continue;
    nodes.push({
      id: `po:${s}`,
      stage: 2,
      label: stateLabel[s],
      color: (PALETTE as Record<string, string>)[s] || PALETTE.other,
      value: v,
      x: 0,
      y: 0,
      height: 0,
    });
  }
  for (const s of [...STATES_OF_INTEREST, 'other'] as const) {
    const v = stateToWoCount[s] || 0;
    if (v === 0) continue;
    nodes.push({
      id: `wo:${s}`,
      stage: 3,
      label: `${stateLabel[s]} WOs`,
      color: (PALETTE as Record<string, string>)[s] || PALETTE.other,
      value: v,
      x: 0,
      y: 0,
      height: 0,
    });
  }

  // ----- links -----
  const links: SLink[] = [];
  if (rawToSubBom > 0) links.push({ source: 'in:raw', target: 'bom:sub', value: rawToSubBom });
  if (rawToFinalBom > 0)
    links.push({ source: 'in:raw', target: 'bom:final', value: rawToFinalBom });
  if (subToSubBom > 0) links.push({ source: 'in:sub', target: 'bom:sub', value: subToSubBom });
  if (subToFinalBom > 0)
    links.push({ source: 'in:sub', target: 'bom:final', value: subToFinalBom });
  for (const s of [...STATES_OF_INTEREST, 'other']) {
    if (subBomToState[s] > 0)
      links.push({ source: 'bom:sub', target: `po:${s}`, value: subBomToState[s] });
    if (finalBomToState[s] > 0)
      links.push({ source: 'bom:final', target: `po:${s}`, value: finalBomToState[s] });
    if (stateToWoCount[s] > 0)
      links.push({ source: `po:${s}`, target: `wo:${s}`, value: stateToWoCount[s] });
  }

  // ----- node sizing -----
  // Stage 1→2 and 2→3 cross unit boundaries (lines → POs → WOs). To keep the
  // diagram visually honest, set each node's height to the LARGER of its
  // incoming-flow sum and outgoing-flow sum, then size each link half (source
  // side / target side) using its own node's scale. The ribbon between two
  // unit-boundary stages is therefore tapered — wide at one end, narrow at
  // the other — which is the standard d3-sankey treatment for unbalanced
  // flows.
  const inSumByNode = new Map<string, number>();
  const outSumByNode = new Map<string, number>();
  for (const l of links) {
    outSumByNode.set(l.source, (outSumByNode.get(l.source) || 0) + l.value);
    inSumByNode.set(l.target, (inSumByNode.get(l.target) || 0) + l.value);
  }
  for (const n of nodes) {
    const inV = inSumByNode.get(n.id) || 0;
    const outV = outSumByNode.get(n.id) || 0;
    // Override the seed value with the side that drives the layout. Falls
    // back to the original value when the node is detached (no in or out).
    n.value = Math.max(inV, outV) || n.value;
  }

  // ----- layout -----
  const stageNodes: SNode[][] = [[], [], [], []];
  for (const n of nodes) stageNodes[n.stage].push(n);
  // Use a per-stage scale so each stage uses the full vertical band even
  // when totals differ across stages. Per-side link scaling (above) keeps
  // ribbons proportional within each node.
  const innerH = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const NODE_GAP = 14;
  for (let stage = 0; stage < 4; stage++) {
    const x = PAD_LEFT + stage * STAGE_GAP;
    const ns = stageNodes[stage];
    const stageTotal = ns.reduce((acc, n) => acc + n.value, 0) || 1;
    const availableH = innerH - (ns.length - 1) * NODE_GAP;
    let cumY = PAD_TOP;
    for (const n of ns) {
      n.x = x;
      n.height = (n.value / stageTotal) * availableH;
      n.y = cumY;
      cumY += n.height + NODE_GAP;
    }
  }

  return { nodes, links };
}

// For each stage, choose where to anchor each label's text baseline so two
// labels never overlap. Returns a parallel array of label Y coordinates
// matching the nodes argument (callers also use it to draw a leader line
// from the node center to the chosen label position).
function placeLabels(nodes: SNode[]): number[] {
  // Sort indexes by node.y ascending so the sweep is monotonic.
  const idx = nodes.map((_, i) => i).sort((a, b) => nodes[a].y - nodes[b].y);
  const labelY = new Array(nodes.length).fill(0);
  let prevBottom = -Infinity;
  for (const i of idx) {
    const n = nodes[i];
    const want = n.y + n.height / 2;
    const placed = Math.max(want, prevBottom);
    labelY[i] = placed;
    prevBottom = placed + LABEL_LINE_H;
  }
  return labelY;
}

interface PositionedLink extends SLink {
  sourceX: number;
  sourceY1: number; // top of ribbon at source
  sourceY2: number; // bottom of ribbon at source
  targetX: number;
  targetY1: number;
  targetY2: number;
  color: string;
}

function positionLinks(nodes: SNode[], links: SLink[]): PositionedLink[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  // Per-side outgoing/incoming totals so we can scale each end of a ribbon
  // by its own node's units (lines vs POs vs WOs).
  const outSum = new Map<string, number>();
  const inSum = new Map<string, number>();
  for (const l of links) {
    outSum.set(l.source, (outSum.get(l.source) || 0) + l.value);
    inSum.set(l.target, (inSum.get(l.target) || 0) + l.value);
  }
  // Cumulative offsets at the right side of source and left side of target,
  // independent because the units differ.
  const sourceCum = new Map<string, number>();
  const targetCum = new Map<string, number>();
  // Stable ordering: stack by (source.y, target.y) so ribbons don't cross
  // unnecessarily.
  const sorted = [...links].sort((a, b) => {
    const sa = byId.get(a.source);
    const sb = byId.get(b.source);
    if (sa && sb && sa.y !== sb.y) return sa.y - sb.y;
    const ta = byId.get(a.target);
    const tb = byId.get(b.target);
    if (ta && tb) return ta.y - tb.y;
    return 0;
  });
  const out: PositionedLink[] = [];
  for (const l of sorted) {
    const s = byId.get(l.source);
    const t = byId.get(l.target);
    if (!s || !t) continue;
    const sScale = s.height / Math.max(outSum.get(s.id) || 1, 1);
    const tScale = t.height / Math.max(inSum.get(t.id) || 1, 1);
    const sH = Math.max(1, l.value * sScale);
    const tH = Math.max(1, l.value * tScale);
    const sCum = sourceCum.get(s.id) || 0;
    const tCum = targetCum.get(t.id) || 0;
    sourceCum.set(s.id, sCum + sH);
    targetCum.set(t.id, tCum + tH);
    out.push({
      ...l,
      sourceX: s.x + NODE_W,
      sourceY1: s.y + sCum,
      sourceY2: s.y + sCum + sH,
      targetX: t.x,
      targetY1: t.y + tCum,
      targetY2: t.y + tCum + tH,
      color: s.color,
    });
  }
  return out;
}

// Filled tapered bezier ribbon. Two horizontal edges (top + bottom) curve
// from source-side y-band to target-side y-band, closed and filled — this
// is the standard Sankey ribbon shape for unbalanced flows.
function linkPath(p: PositionedLink): string {
  const cx = p.sourceX + (p.targetX - p.sourceX) * 0.5;
  return [
    `M ${p.sourceX} ${p.sourceY1}`,
    `C ${cx} ${p.sourceY1}, ${cx} ${p.targetY1}, ${p.targetX} ${p.targetY1}`,
    `L ${p.targetX} ${p.targetY2}`,
    `C ${cx} ${p.targetY2}, ${cx} ${p.sourceY2}, ${p.sourceX} ${p.sourceY2}`,
    'Z',
  ].join(' ');
}

export function ManufacturingFlow({ boms, productionOrders }: Props) {
  const { nodes, links } = useMemo(
    () => buildModel(boms, productionOrders),
    [boms, productionOrders]
  );
  const positioned = useMemo(() => positionLinks(nodes, links), [nodes, links]);

  // Per-stage label placement so labels never overlap.
  const labelYs = useMemo(() => {
    const result = new Map<string, number>();
    for (let stage = 0; stage < 4; stage++) {
      const stageNodes = nodes.filter((n) => n.stage === stage);
      const ys = placeLabels(stageNodes);
      stageNodes.forEach((n, i) => result.set(n.id, ys[i]));
    }
    return result;
  }, [nodes]);

  const totalW = PAD_LEFT + (STAGES.length - 1) * STAGE_GAP + NODE_W + PAD_RIGHT;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 3, bgcolor: '#fafbfc' }}
      data-testid="manufacturing-flow"
    >
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Manufacturing pipeline
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Sankey of the BOM → production-order → work-order flow. Bar height = item count; ribbon
        thickness = how many cross from one stage to the next.
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <svg
          width={totalW}
          height={HEIGHT}
          viewBox={`0 0 ${totalW} ${HEIGHT}`}
          style={{ display: 'block' }}
        >
          {/* Stage headers */}
          {STAGES.map((s, i) => {
            const x = PAD_LEFT + i * STAGE_GAP + NODE_W / 2;
            return (
              <text
                key={s.id}
                x={x}
                y={18}
                textAnchor="middle"
                fontSize={12}
                fontWeight={600}
                fill="#475569"
                letterSpacing={0.5}
              >
                {s.label.toUpperCase()}
              </text>
            );
          })}

          {/* Links underneath nodes — filled tapered bezier ribbons. */}
          <g>
            {positioned.map((p, i) => (
              <path
                key={`link-${i}`}
                d={linkPath(p)}
                fill={p.color}
                fillOpacity={0.35}
                stroke="none"
              />
            ))}
          </g>

          {/* Nodes + labels */}
          <g>
            {nodes.map((n) => {
              const h = Math.max(2, n.height);
              const labelY = labelYs.get(n.id) ?? n.y + h / 2;
              const nodeCenterY = n.y + h / 2;
              const onLeft = n.stage === 0;
              const labelX = onLeft ? n.x - 12 : n.x + NODE_W + 12;
              const leaderEndX = onLeft ? n.x - 4 : n.x + NODE_W + 4;
              const labelTextY = labelY - 4;
              const valueTextY = labelY + 12;
              const showLeader = Math.abs(labelY - nodeCenterY) > 2;
              return (
                <g key={n.id}>
                  <rect x={n.x} y={n.y} width={NODE_W} height={h} rx={4} fill={n.color} />
                  {showLeader && (
                    <path
                      d={`M ${
                        onLeft ? n.x : n.x + NODE_W
                      } ${nodeCenterY} L ${leaderEndX} ${nodeCenterY} L ${
                        labelX + (onLeft ? 4 : -4)
                      } ${labelY} L ${labelX} ${labelY}`}
                      fill="none"
                      stroke="#cbd5e1"
                      strokeWidth={1}
                    />
                  )}
                  <text
                    x={labelX}
                    y={labelTextY}
                    textAnchor={onLeft ? 'end' : 'start'}
                    fontSize={13}
                    fontWeight={600}
                    fill="#0f172a"
                  >
                    {n.label}
                  </text>
                  <text
                    x={labelX}
                    y={valueTextY}
                    textAnchor={onLeft ? 'end' : 'start'}
                    fontSize={11}
                    fill="#64748b"
                  >
                    {n.value.toLocaleString()}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      </Box>
    </Paper>
  );
}
